import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Mail } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/errors";

/**
 * Public marketing unsubscribe page.
 *
 * URL: /unsubscribe?token=<signed>
 *
 * - Loads /api/marketing/unsubscribe?token=... on mount to show the recipient's
 *   email + which agency's list they're leaving.
 * - One-click Confirm → POST /api/marketing/unsubscribe.
 * - Marketing-only: quote emails and account emails still fire.
 */
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, info: null, error: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, info: null, error: "This unsubscribe link is missing its token." });
      return;
    }
    api
      .get("/marketing/unsubscribe", { params: { token } })
      .then(({ data }) => setState({ loading: false, info: data, error: "" }))
      .catch((e) => setState({ loading: false, info: null, error: formatApiError(e) }));
  }, [token]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await api.post("/marketing/unsubscribe", { token });
      setDone(true);
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="bg-slate-900 text-white px-6 py-5">
          <div className="text-[11px] uppercase tracking-[0.14em] font-mono text-slate-400">
            Marketing preferences
          </div>
          <h1 className="text-xl font-bold mt-1">Unsubscribe</h1>
        </div>

        <div className="p-6">
          {state.loading && (
            <div className="text-center text-sm text-slate-500 py-6 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your link…
            </div>
          )}

          {!state.loading && state.error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800 flex items-start gap-3" data-testid="marketing-unsub-error">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">Couldn't process this link</div>
                <div className="text-red-700 text-[13px]">{state.error}</div>
                <div className="text-[12px] text-red-600 mt-2">
                  If you copied it from an email, try opening the link directly from your inbox.
                </div>
              </div>
            </div>
          )}

          {!state.loading && state.info && !done && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                You're about to unsubscribe{" "}
                <span className="font-mono font-semibold text-slate-900" data-testid="marketing-unsub-email">
                  {state.info.email}
                </span>{" "}
                from marketing emails sent by{" "}
                <span className="font-semibold">{state.info.agency_name}</span>.
              </p>

              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-800 leading-relaxed">
                <strong>Heads up:</strong> this only stops <em>marketing</em>. You'll still receive
                account emails, quote confirmations, and password resets. This does not
                cancel any distributor account you may have.
              </div>

              {state.info.already_unsubscribed && (
                <div className="text-[13px] text-slate-500 italic">
                  It looks like you already unsubscribed once. Confirming again is safe.
                </div>
              )}

              <Button
                onClick={handleConfirm}
                disabled={submitting}
                className="w-full"
                data-testid="marketing-unsub-confirm-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unsubscribing…
                  </>
                ) : (
                  "Confirm unsubscribe"
                )}
              </Button>
            </div>
          )}

          {done && (
            <div className="text-center space-y-3 py-2" data-testid="marketing-unsub-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <div className="text-lg font-semibold text-slate-900">You're unsubscribed</div>
              <p className="text-sm text-slate-600">
                We won't send marketing emails to{" "}
                <span className="font-mono">{state.info?.email}</span> anymore. If you change
                your mind later, reply to any past email and we'll add you back.
              </p>
              <div className="pt-4 text-[11px] text-slate-400 font-mono uppercase tracking-[0.1em] flex items-center justify-center gap-1">
                <Mail className="w-3 h-3" /> My Rep Agency
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
